import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { FaTrash, FaArrowUp, FaArrowDown, FaInfoCircle } from 'react-icons/fa';
import {
  getTvPanelSettings,
  uploadTvPanelBackground,
  deleteTvPanelBackground,
  uploadTvPanelEmblem,
  deleteTvPanelEmblem,
  setTvPanelEmblemVisibility,
  uploadTvPanelSsFiles,
  deleteTvPanelSsFile,
  updateTvPanelSsFile,
  updateTvPanelSsFilesOrder,
  saveTvPanelSsSettings,
} from '../../../../actions/tvPanel';
import { store } from '../../../../components/App';
import CustomCheckbox from '../../../../assets/CustomCheckbox/CustomCheckbox';
import Confirmation from '../../../../assets/Confirmation/Confirmation';
import Loader from 'react-loader';
import { BACKEND_URL_PREFIX } from '../../../../utils/const';
import styles from './styles.module.scss';

const useAuthImage = (url) => {
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    if (!url) { setBlobUrl(null); return; }
    let revoked = false;
    const token = store.getState().auth.token;
    const fullUrl = `${BACKEND_URL_PREFIX}/${url}`;
    fetch(fullUrl, {
      headers: { Authorization: `Basic ${btoa(`${token}:empty`)}` }
    })
      .then(r => r.ok ? r.blob() : null)
      .then(blob => {
        if (blob && !revoked) {
          const objUrl = URL.createObjectURL(blob);
          setBlobUrl(objUrl);
        }
      })
      .catch(() => {});
    return () => {
      revoked = true;
      setBlobUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    };
  }, [url]);

  return blobUrl;
};

const BG_ALLOWED_EXT = ['jpg', 'jpeg', 'png', 'webp', 'gif'];
const BG_ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const EMBLEM_ALLOWED_EXT = ['png', 'webp'];
const EMBLEM_ALLOWED_MIME = ['image/png', 'image/webp'];
const SS_ALLOWED_EXT = ['jpg', 'jpeg', 'png', 'webp', 'mp4', 'webm', 'mov'];
const SS_ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp', 'video/mp4', 'video/webm', 'video/quicktime'];

const TvPanelTab = ({
  updateRight,
  settings,
  settingsLoading,
  bgUploading,
  emblemUploading,
  ssFiles,
  ssFilesLoading,
  ssFileUploading,
  ssSettings,
  ssSettingsLoading,
  getTvPanelSettings,
  uploadTvPanelBackground,
  deleteTvPanelBackground,
  uploadTvPanelEmblem,
  deleteTvPanelEmblem,
  setTvPanelEmblemVisibility,
  uploadTvPanelSsFiles,
  deleteTvPanelSsFile,
  updateTvPanelSsFile,
  updateTvPanelSsFilesOrder,
  saveTvPanelSsSettings,
}) => {
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showDeleteBg, setShowDeleteBg] = useState(false);
  const [showDeleteEmblem, setShowDeleteEmblem] = useState(false);
  const [localSsSettings, setLocalSsSettings] = useState({ idle_sec: 300, slide_sec: 10 });
  const [ssSettingsErrors, setSsSettingsErrors] = useState({});
  const [emblemTooltipVisible, setEmblemTooltipVisible] = useState(false);
  const tooltipRef = useRef(null);

  useEffect(() => {
    getTvPanelSettings();
  }, [getTvPanelSettings]);

  useEffect(() => {
    if (ssSettings) {
      setLocalSsSettings(ssSettings);
    }
  }, [ssSettings]);

  // Close tooltip on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target)) {
        setEmblemTooltipVisible(false);
      }
    };
    if (emblemTooltipVisible) {
      document.addEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [emblemTooltipVisible]);

  // ---- Background ----
  const handleBgSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!BG_ALLOWED_EXT.includes(ext) || !BG_ALLOWED_MIME.includes(file.type)) {
      alert(`Недопустимый формат файла. Разрешены: ${BG_ALLOWED_EXT.join(', ')}`);
      e.target.value = '';
      return;
    }
    uploadTvPanelBackground(file);
    e.target.value = '';
  };

  // ---- Emblem ----
  const handleEmblemSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!EMBLEM_ALLOWED_EXT.includes(ext) || !EMBLEM_ALLOWED_MIME.includes(file.type)) {
      alert(`Недопустимый формат файла. Разрешены: ${EMBLEM_ALLOWED_EXT.join(', ')}`);
      e.target.value = '';
      return;
    }
    uploadTvPanelEmblem(file);
    e.target.value = '';
  };

  // ---- Screensaver Files ----
  const handleSsFilesSelect = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const invalid = files.filter(f => {
      const ext = f.name.split('.').pop().toLowerCase();
      return !SS_ALLOWED_EXT.includes(ext) || !SS_ALLOWED_MIME.includes(f.type);
    });
    if (invalid.length) {
      alert(`Недопустимые файлы: ${invalid.map(f => f.name).join(', ')}. Разрешены: ${SS_ALLOWED_EXT.join(', ')}`);
      e.target.value = '';
      return;
    }

    uploadTvPanelSsFiles(files);
    e.target.value = '';
  };

  const handleSsActiveToggle = (fileId, isActive) => {
    updateTvPanelSsFile(fileId, { is_active: isActive });
  };

  const moveSsFile = (index, direction) => {
    const activeFiles = ssFiles
    // .filter(f => f.is_active);
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= activeFiles.length) return;

    const newFiles = [...activeFiles];
    [newFiles[index], newFiles[newIndex]] = [newFiles[newIndex], newFiles[index]];

    const orderData = newFiles.map((f, idx) => ({ id: f.id, display_order: idx + 1 }));
    updateTvPanelSsFilesOrder(orderData);
  };

  // ---- Screensaver Settings ----
  const validateSsSettings = () => {
    const errors = {};
    if (!localSsSettings.idle_sec || localSsSettings.idle_sec < 10) {
      errors.idle_sec = 'Минимум 10 секунд';
    }
    if (!localSsSettings.slide_sec || localSsSettings.slide_sec < 3) {
      errors.slide_sec = 'Минимум 3 секунды';
    }
    setSsSettingsErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSsSettingsChange = (field, value) => {
    const trimmedValue = value.slice(0, 10);
    setLocalSsSettings(prev => ({ ...prev, [field]: parseInt(trimmedValue, 10) || '' }));
    if (ssSettingsErrors[field]) {
      setSsSettingsErrors(prev => { const e = { ...prev }; delete e[field]; return e; });
    }
  };

  const handleSaveSsSettings = () => {
    if (!validateSsSettings()) return;
    saveTvPanelSsSettings(localSsSettings);
  };

  // ---- Helpers ----
  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('ru-RU');
  };

  const getFileTypeLabel = (mimeType) => {
    if (mimeType?.startsWith('image/')) return 'Изображение';
    if (mimeType?.startsWith('video/')) return 'Видео';
    return mimeType || 'Неизвестно';
  };

  const bg = settings?.background;
  const emblem = settings?.emblem;

  // Hooks must be called unconditionally (before any early return)
  const bgPreviewUrl = useAuthImage(bg?.has_custom ? `tv_panel/background/file?t=${bg.uploaded_at}` : null);
  const emblemPreviewUrl = useAuthImage(emblem?.has_custom ? `tv_panel/emblem/file?t=${emblem.uploaded_at}` : null);

  if (settingsLoading && !settings) {
    return <div className={styles.loader}><Loader color="#7b2b2a" loaded={false} /></div>;
  }

  const activeFiles = ssFiles.filter(f => f.is_active);
  const inactiveFiles = ssFiles.filter(f => !f.is_active);
  const sortedFiles = [...activeFiles, ...inactiveFiles];

  return (
    <div className={styles.tvPanelTab}>

      {/* ===== Основной экран ===== */}
      <section className={styles.block}>
        <h3 className={styles.blockTitle}>Основной экран</h3>
        <p className={styles.blockDesc}>
          Настройте изображение, которое будет использоваться на главном экране мультимедийной панели.
          Если файл не загружен, будет использоваться стандартный фон.
        </p>

        <div className={styles.assetRow}>
          <div className={`${styles.preview} ${styles.emblemPreview}`}>
            {bgPreviewUrl ? (
              <img
                src={bgPreviewUrl}
                alt="Фон"
                className={styles.bgPreviewImg}
                onClick={() => window.open(bgPreviewUrl, '_blank')}
                style={{ cursor: 'pointer' }}
              />
            ) : (
              <div className={styles.previewPlaceholder}>Используется стандартный фон</div>
            )}
          </div>

          <div className={styles.assetInfo}>
            {bg?.has_custom && (
              <div className={styles.fileInfo}>
                <span>{bg.file_name}</span>
                <span className={styles.fileMeta}>{formatDate(bg.uploaded_at)}</span>
              </div>
            )}

            {updateRight && (
              <div className={styles.assetActions}>
                <label htmlFor="bgFileInput" className={`${styles.uploadBtn} ${bgUploading ? styles.disabled : ''}`}>
                  {bgUploading ? 'Загрузка...' : 'Загрузить фон экрана'}
                </label>
                <input
                  id="bgFileInput"
                  type="file"
                  accept={BG_ALLOWED_MIME.join(',')}
                  onChange={handleBgSelect}
                  disabled={bgUploading}
                  hidden
                />
                {bg?.has_custom && (
                  <button
                    type="button"
                    className={styles.deleteAssetBtn}
                    onClick={() => setShowDeleteBg(true)}
                    title="Вернуть стандартный фон"
                  >
                    <FaTrash /> Удалить
                  </button>
                )}
              </div>
            )}

            <div className={styles.hint}>Допустимые форматы: jpg, jpeg, png, webp, gif</div>
          </div>
        </div>
      </section>

      {/* ===== Знак рода войск ===== */}
      <section className={styles.block}>
        <div className={styles.blockTitleRow}>
          <h3 className={styles.blockTitle}>Знак рода войск</h3>
          <div className={styles.tooltipWrapper} ref={tooltipRef}>
            <FaInfoCircle
              className={styles.infoIcon}
              onMouseEnter={() => setEmblemTooltipVisible(true)}
              onMouseLeave={() => setEmblemTooltipVisible(false)}
              onClick={() => setEmblemTooltipVisible(v => !v)}
            />
            {emblemTooltipVisible && (
              <div className={styles.tooltip}>
                Файл должен быть без фона и содержать только знак рода войск. Рекомендуется использовать PNG или WEBP
                с прозрачностью. Не добавляйте сплошной фон, рамки, надписи и лишние пустые поля. Если пользовательский
                знак не загружен, при включённом отображении будет использоваться стандартный знак.
              </div>
            )}
          </div>
        </div>

        {updateRight ? (
          <label className={styles.toggleLabel}>
            <CustomCheckbox
              checked={emblem?.enabled ?? true}
              onChange={(e) => setTvPanelEmblemVisibility(e.target.checked)}
            />
            <span>Отображать знак рода войск</span>
          </label>
        ) : (
          <div className={styles.toggleLabel}>
            <CustomCheckbox checked={emblem?.enabled ?? true} disabled />
            <span>Отображать знак рода войск</span>
          </div>
        )}

        <div className={styles.assetRow}>
          <div className={`${styles.preview} ${styles.emblemPreview}`}>
            {emblemPreviewUrl ? (
              <img
                src={emblemPreviewUrl}
                alt="Знак"
                className={styles.emblemPreviewImg}
                onClick={() => window.open(emblemPreviewUrl, '_blank')}
                style={{ cursor: 'pointer' }}
              />
            ) : (
              <div className={styles.previewPlaceholder}>
                {emblem?.enabled ? 'Используется стандартный знак' : 'Знак скрыт'}
              </div>
            )}
          </div>

          <div className={styles.assetInfo}>
            {emblem?.has_custom && (
              <div className={styles.fileInfo}>
                <span>{emblem.file_name}</span>
                <span className={styles.fileMeta}>{formatDate(emblem.uploaded_at)}</span>
              </div>
            )}

            {updateRight && (
              <div className={styles.assetActions}>
                <label htmlFor="emblemFileInput" className={`${styles.uploadBtn} ${emblemUploading ? styles.disabled : ''}`}>
                  {emblemUploading ? 'Загрузка...' : 'Загрузить знак рода войск'}
                </label>
                <input
                  id="emblemFileInput"
                  type="file"
                  accept={EMBLEM_ALLOWED_MIME.join(',')}
                  onChange={handleEmblemSelect}
                  disabled={emblemUploading}
                  hidden
                />
                {emblem?.has_custom && (
                  <button
                    type="button"
                    className={styles.deleteAssetBtn}
                    onClick={() => setShowDeleteEmblem(true)}
                    title="Вернуть стандартный знак"
                  >
                    <FaTrash /> Удалить
                  </button>
                )}
              </div>
            )}

            <div className={styles.hint}>Допустимые форматы: png, webp</div>
          </div>
        </div>
      </section>

      {/* ===== Заставка при засыпании ===== */}
      <section className={styles.block}>
        <h3 className={styles.blockTitle}>Заставка при засыпании</h3>

        {/* Settings */}
        <div className={styles.ssSettingsForm}>
          <div className={`${styles.formGroup} ${ssSettingsErrors.idle_sec ? styles.inputError : ''}`}>
            <label htmlFor="idle_sec">Время до запуска заставки (секунды)</label>
            <input
              id="idle_sec"
              type="number"
              min={10}
              maxLength={10}
              value={localSsSettings.idle_sec}
              onChange={(e) => handleSsSettingsChange('idle_sec', e.target.value)}
              disabled={!updateRight}
            />
            {ssSettingsErrors.idle_sec && <div className={styles.errorText}>{ssSettingsErrors.idle_sec}</div>}
            <div className={styles.hint}>Минимум 10 секунд</div>
          </div>

          <div className={`${styles.formGroup} ${ssSettingsErrors.slide_sec ? styles.inputError : ''}`}>
            <label htmlFor="slide_sec">Время показа слайдов (секунды)</label>
            <input
              id="slide_sec"
              type="number"
              min={3}
              maxLength={10}
              value={localSsSettings.slide_sec}
              onChange={(e) => handleSsSettingsChange('slide_sec', e.target.value)}
              disabled={!updateRight}
            />
            {ssSettingsErrors.slide_sec && <div className={styles.errorText}>{ssSettingsErrors.slide_sec}</div>}
            <div className={styles.hint}>Минимум 3 секунды</div>
          </div>

          {updateRight && (
            <button
              type="button"
              className={styles.saveSettingsBtn}
              onClick={handleSaveSsSettings}
              disabled={ssSettingsLoading}
            >
              {ssSettingsLoading ? 'Сохранение...' : 'Сохранить настройки'}
            </button>
          )}
        </div>

        {/* Files */}
        <div className={styles.ssFilesHeader}>
          {updateRight && (
            <div className={styles.uploadSection}>
              <label htmlFor="ssFilesInput" className={`${styles.uploadBtn} ${ssFileUploading ? styles.disabled : ''}`}>
                {ssFileUploading ? 'Загрузка...' : 'Добавить файлы заставки'}
              </label>
              <input
                id="ssFilesInput"
                type="file"
                multiple
                accept={SS_ALLOWED_MIME.join(',')}
                onChange={handleSsFilesSelect}
                disabled={ssFileUploading}
                hidden
              />
              <span className={styles.hint}>Форматы: {SS_ALLOWED_EXT.join(', ')}</span>
            </div>
          )}
        </div>

        {ssFilesLoading && !ssFiles.length ? (
          <div className={styles.loader}><Loader color="#7b2b2a" loaded={false} /></div>
        ) : sortedFiles.length > 0 ? (
          <table className={styles.filesTable}>
            <thead>
              <tr>
                <th>Имя файла</th>
                <th style={{ width: 120 }}>Тип</th>
                <th style={{ width: 100 }}>Размер</th>
                <th style={{ width: 80 }}>Активен</th>
                <th style={{ width: 160 }}>Загружен</th>
                <th style={{ width: 140 }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {sortedFiles.map((file) => {
                const activeIndex = file.is_active ? activeFiles.findIndex(f => f.id === file.id) : -1;
                return (
                  <tr key={file.id}>
                    <td className={styles.fileNameCell} title={file.file_name}>{file.file_name}</td>
                    <td><span className={styles.fileType}>{getFileTypeLabel(file.mime_type)}</span></td>
                    <td>{formatFileSize(file.file_size)}</td>
                    <td>
                      <CustomCheckbox
                        checked={file.is_active}
                        onChange={(e) => handleSsActiveToggle(file.id, e.target.checked)}
                        disabled={!updateRight}
                      />
                    </td>
                    <td>{formatDate(file.uploaded_at)}</td>
                    <td>
                      <div className={styles.rowActions}>
                        {file.is_active && (
                          <>
                            <button
                              type="button"
                              onClick={() => moveSsFile(activeIndex, -1)}
                              disabled={activeIndex === 0 || !updateRight}
                              title="Вверх"
                            >
                              <FaArrowUp />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveSsFile(activeIndex, 1)}
                              disabled={activeIndex === activeFiles.length - 1 || !updateRight}
                              title="Вниз"
                            >
                              <FaArrowDown />
                            </button>
                          </>
                        )}
                        {updateRight && (
                          <button
                            type="button"
                            className={styles.deleteBtn}
                            onClick={() => setDeleteConfirm(file)}
                            title="Удалить"
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className={styles.emptyMessage}>Нет загруженных файлов</p>
        )}
      </section>

      {/* Confirmation dialogs */}
      {showDeleteBg && (
        <Confirmation
          title="Удалить пользовательский фон?"
          acceptConfirmation={() => { deleteTvPanelBackground(); setShowDeleteBg(false); }}
          rejectConfirmation={() => setShowDeleteBg(false)}
        />
      )}
      {showDeleteEmblem && (
        <Confirmation
          title="Удалить пользовательский знак?"
          acceptConfirmation={() => { deleteTvPanelEmblem(); setShowDeleteEmblem(false); }}
          rejectConfirmation={() => setShowDeleteEmblem(false)}
        />
      )}
      {deleteConfirm && (
        <Confirmation
          title={`Удалить файл "${deleteConfirm.file_name}"?`}
          acceptConfirmation={() => { deleteTvPanelSsFile(deleteConfirm.id); setDeleteConfirm(null); }}
          rejectConfirmation={() => setDeleteConfirm(null)}
        />
      )}
    </div>
  );
};

const mapStateToProps = ({ tvPanel }) => ({
  settings: tvPanel.settings,
  settingsLoading: tvPanel.settingsLoading,
  bgUploading: tvPanel.bgUploading,
  emblemUploading: tvPanel.emblemUploading,
  ssFiles: tvPanel.ssFiles,
  ssFilesLoading: tvPanel.ssFilesLoading,
  ssFileUploading: tvPanel.ssFileUploading,
  ssSettings: tvPanel.ssSettings,
  ssSettingsLoading: tvPanel.ssSettingsLoading,
});

export default connect(mapStateToProps, {
  getTvPanelSettings,
  uploadTvPanelBackground,
  deleteTvPanelBackground,
  uploadTvPanelEmblem,
  deleteTvPanelEmblem,
  setTvPanelEmblemVisibility,
  uploadTvPanelSsFiles,
  deleteTvPanelSsFile,
  updateTvPanelSsFile,
  updateTvPanelSsFilesOrder,
  saveTvPanelSsSettings,
})(TvPanelTab);
